import { z } from 'zod'
import { FIELD_MAX_LENGTH, FREE_TEXT_FIELDS } from './field-limits.ts'
import type { WizardFormValues } from './wizard-draft.ts'

const KTP_REGEX = /^\d{16}$/
const NPWP_DIGITS_REGEX = /^\d{15,16}$/

export function validateKtp(value: string): boolean {
  return KTP_REGEX.test(value.replace(/\s/g, ''))
}

export function validateNpwp(value: string): boolean {
  const digits = value.replace(/[.\-]/g, '')
  return NPWP_DIGITS_REGEX.test(digits)
}

export function normalizeNpwp(value: string): string {
  return value.replace(/[.\-\s]/g, '')
}

/** MetaTrader trading password: 8-64 chars with upper, lower, digit, and symbol. */
export function validateMtPassword(value: string): boolean {
  if (value.length < 8 || value.length > 64) return false
  return /[A-Z]/.test(value) && /[a-z]/.test(value) && /\d/.test(value) && /[^A-Za-z0-9]/.test(value)
}

export const ktpSchema = z
  .string()
  .min(1, 'required')
  .refine(validateKtp, { message: 'invalidKtp' })

export const npwpSchema = z
  .string()
  .optional()
  .refine((v) => !v || validateNpwp(v), { message: 'invalidNpwp' })

export const mtPasswordSchema = z
  .string()
  .min(8, 'minLength')
  .max(64, 'maxLength')
  .refine(validateMtPassword, { message: 'invalidMtPassword' })

const NAME_PUNCT = new Set([" ", ".", "'", "-"])

export function validPersonName(value: string): boolean {
  const text = value.trim()
  if (text.length < 2 || text.length > 100) return false
  for (const ch of text) {
    if (NAME_PUNCT.has(ch) || ch === ' ') continue
    if (!/\p{L}/u.test(ch)) return false
  }
  return true
}

export function validBirthDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const born = new Date(`${value}T00:00:00`)
  if (Number.isNaN(born.getTime())) return false
  const today = new Date()
  const year = born.getFullYear()
  if (year <= 1900 || born > today) return false
  let age = today.getFullYear() - year
  const monthDay = today.getMonth() - born.getMonth()
  if (monthDay < 0 || (monthDay === 0 && today.getDate() < born.getDate())) age -= 1
  return age >= 18
}

export function validPostalCode(value: string): boolean {
  return /^\d{5}$/.test(value.trim())
}

/** ponytail: 8–16 digits for every bank; no per-bank length table. Upgrade: map bank name to length. */
export function validAccountNumber(value: string): boolean {
  return /^\d{8,16}$/.test(value.trim())
}

/** Home phone: 0 / 62 / +62 then 7-12 digits; spaces, dashes and brackets allowed. Same rule as the server. */
export function validHomePhone(value: string): boolean {
  const text = value.trim()
  if (text.length > 20) return false
  return /^(?:\+62|62|0)[1-9]\d{6,11}$/.test(text.replace(/[\s\-()]/g, ''))
}

/** Free text: at most `max` characters, no < or >, no control characters except tab and newlines. */
export function validFreeText(value: string, max: number): 'tooLong' | 'invalidText' | null {
  const text = value.trim()
  if (text.length > max) return 'tooLong'
  for (const ch of text) {
    const code = ch.charCodeAt(0)
    if (ch === '<' || ch === '>') return 'invalidText'
    if ((code < 0x20 && code !== 9 && code !== 10 && code !== 13) || (code >= 0x7f && code <= 0x9f)) {
      return 'invalidText'
    }
  }
  return null
}

export type WizardIssueKey =
  | 'invalidName'
  | 'invalidKtp'
  | 'invalidDob'
  | 'invalidPostal'
  | 'invalidAccountNumber'
  | 'invalidAmount'
  | 'accountHolderMismatch'
  | 'tooLong'
  | 'invalidText'
  | 'invalidHomePhone'

export function wizardIssueKeys(
  values: WizardFormValues,
): Partial<Record<string, WizardIssueKey>> {
  const issues: Partial<Record<string, WizardIssueKey>> = {}
  const names: Array<[string, string]> = [
    ['full_name', values.full_name],
    ['birth_place', values.birth_place],
    ['mother_maiden_name', values.mother_maiden_name],
    ['spouse_name', values.spouse_name],
    ['emergency_name', values.emergency_name],
    ['account_holder', values.account_holder],
  ]
  for (const [key, raw] of names) {
    if (raw.trim() && !validPersonName(raw)) issues[key] = 'invalidName'
  }
  if (values.identity_number.trim() && !validateKtp(values.identity_number)) issues.identity_number = 'invalidKtp'
  if (values.birth_date.trim() && !validBirthDate(values.birth_date)) issues.birth_date = 'invalidDob'
  if (values.home_postal_code.trim() && !validPostalCode(values.home_postal_code)) {
    issues.home_postal_code = 'invalidPostal'
  }
  if (values.office_postal_code.trim() && !validPostalCode(values.office_postal_code)) {
    issues.office_postal_code = 'invalidPostal'
  }
  if (values.emergency_postal_code.trim() && !validPostalCode(values.emergency_postal_code)) {
    issues.emergency_postal_code = 'invalidPostal'
  }
  if (values.account_number.trim() && !validAccountNumber(values.account_number)) {
    issues.account_number = 'invalidAccountNumber'
  }
  if (!issues.account_holder && accountHolderMismatch(values.full_name, values.account_holder)) {
    issues.account_holder = 'accountHolderMismatch'
  }
  for (const key of ['njop_value', 'total_savings', 'total_assets'] as const) {
    const amount = values[key]
    if (Number.isFinite(amount) && amount <= 0) issues[key] = 'invalidAmount'
  }
  if (values.home_phone.trim() && !validHomePhone(values.home_phone)) issues.home_phone = 'invalidHomePhone'
  for (const [key, max] of Object.entries(FIELD_MAX_LENGTH)) {
    if (issues[key]) continue
    const raw = values[key as keyof WizardFormValues]
    if (typeof raw !== 'string' || !raw.trim()) continue
    if (FREE_TEXT_FIELDS.has(key)) {
      const issue = validFreeText(raw, max)
      if (issue) issues[key] = issue
    } else if (raw.trim().length > max) {
      issues[key] = 'tooLong'
    }
  }
  return issues
}

function holderKey(value: string): string {
  return value.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
}

export function accountHolderMismatch(fullName: string, holder: string): boolean {
  const left = holderKey(fullName)
  const right = holderKey(holder)
  return Boolean(left && right && left !== right)
}

export const PASSWORD_RULES = {
  minLength: 8,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  digit: /\d/,
  special: /[!@#$%&+\-]/,
} as const

export const signUpPasswordSchema = z
  .string()
  .min(PASSWORD_RULES.minLength, 'minLength')
  .regex(PASSWORD_RULES.uppercase, 'uppercase')
  .regex(PASSWORD_RULES.lowercase, 'lowercase')
  .regex(PASSWORD_RULES.digit, 'digit')
  .regex(PASSWORD_RULES.special, 'special')

/** Indonesian mobile after the +62 prefix: 8 followed by 8–12 digits. */
export function normalizeIdMobile(input: string): string | null {
  let digits = input.replace(/\D/g, '')
  if (digits.startsWith('62')) digits = digits.slice(2)
  if (digits.startsWith('0')) digits = digits.slice(1)
  if (!/^8\d{8,12}$/.test(digits)) return null
  return `+62${digits}`
}
